/**
 * Collects "who reacted?" lookups from every pill rendered in the same tick and sends one
 * POST /workspaces/:ws/reactors for all of them (messages, thread replies, kudos).
 */
import type { Reactor, ReactorsResponse } from '@tradeoutlet/shared';

/** 'entity': feed items' own reactions (t184u); names come from the members list, not the API. */
export type ReactorKind = 'message' | 'kudos' | 'entity';
type Result = Record<string, Reactor[]>;
type Fetcher = (ws: string, b: { message_ids?: string[]; kudos_ids?: string[] }) => Promise<ReactorsResponse>;

export function createReactorBatcher(fetcher: Fetcher, delayMs = 25) {
  const pending = new Map<string, { kind: ReactorKind; id: string; resolve: (r: Result) => void; reject: (e: unknown) => void }[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    timer = null;
    const batches = [...pending]; pending.clear();
    for (const [ws, reqs] of batches) {
      const ids = (k: ReactorKind) => [...new Set(reqs.filter(r => r.kind === k).map(r => r.id))];
      const message_ids = ids('message'), kudos_ids = ids('kudos');
      // The API caps each list at 200; split bigger screens into chunks.
      const chunks: { message_ids: string[]; kudos_ids: string[] }[] = [];
      for (let i = 0; i < Math.max(message_ids.length, kudos_ids.length); i += 200) chunks.push({ message_ids: message_ids.slice(i, i + 200), kudos_ids: kudos_ids.slice(i, i + 200) });
      Promise.all(chunks.map(c => fetcher(ws, { ...(c.message_ids.length ? { message_ids: c.message_ids } : {}), ...(c.kudos_ids.length ? { kudos_ids: c.kudos_ids } : {}) })))
        .then(parts => {
          const merged: ReactorsResponse = { messages: {}, kudos: {} };
          for (const p of parts) { Object.assign(merged.messages, p.messages); Object.assign(merged.kudos, p.kudos); }
          for (const r of reqs) r.resolve((r.kind === 'message' ? merged.messages : merged.kudos)[r.id] ?? {});
        })
        .catch(e => reqs.forEach(r => r.reject(e)));
    }
  };
  return {
    load(ws: string, kind: ReactorKind, id: string): Promise<Result> {
      return new Promise((resolve, reject) => {
        pending.set(ws, [...(pending.get(ws) ?? []), { kind, id, resolve, reject }]);
        if (!timer) timer = setTimeout(flush, delayMs);
      });
    },
  };
}

type EntityFetcher = (ws: string, type: string, ids: string[]) => Promise<{ reactors: Record<string, Result> }>;
/** t255u: feed items' own reactors with names, one POST /threads/:type/reactors per type for a screenful (100 ids a call). */
export function createEntityReactorBatcher(fetcher: EntityFetcher, delayMs = 25) {
  const pending = new Map<string, { id: string; resolve: (r: Result) => void; reject: (e: unknown) => void }[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    timer = null;
    const batches = [...pending]; pending.clear();
    for (const [key, reqs] of batches) {
      const [ws, type] = key.split('|') as [string, string];
      const ids = [...new Set(reqs.map(r => r.id))];
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
      Promise.all(chunks.map(c => fetcher(ws, type, c)))
        .then(parts => { const all = Object.assign({}, ...parts.map(p => p.reactors)) as Record<string, Result>; reqs.forEach(r => r.resolve(all[r.id] ?? {})); })
        .catch(e => reqs.forEach(r => r.reject(e)));
    }
  };
  return {
    load(ws: string, type: string, id: string): Promise<Result> {
      return new Promise((resolve, reject) => {
        const k = `${ws}|${type}`;
        pending.set(k, [...(pending.get(k) ?? []), { id, resolve, reject }]);
        if (!timer) timer = setTimeout(flush, delayMs);
      });
    },
  };
}
